import { t } from "@airange/shared/i18n-semantic-legend";

export type LegendSemantic = (typeof LEGEND_SEMANTIC_KEYS)[number];

export type LegendBadgeStatus = "success" | "processing" | "default" | "error" | "warning";

export const LEGEND_SEMANTIC_KEYS = [
  "all",
  "total",
  "online",
  "offline",
  "unknown",
  "enabled",
  "disabled",
  "running",
  "in_progress",
  "completed",
  "finished",
  "failed",
  "destroyed",
  "warning",
  "attention",
  "missing",
  "stale",
  "draft",
  "healthy",
  "default",
  "normal",
  "watch",
  "active",
  "current",
  "retry",
  "remaining",
  "pending",
  "passed",
  "succeeded",
  "published",
  "archived",
  "public",
  "private",
  "shared_to_users",
  "shared",
  "mine",
  "endpoint",
  "network",
  "security",
  "used",
  "unused",
  "visible",
  "hidden",
  "inactive",
  "degraded",
  "mvp",
  "reserved",
  "container",
  "vm",
  "simulator",
  "topology",
  "traffic_run",
  "traffic_capture",
  "expiring",
  "expired",
  "console",
  "recycles",
  "operator",
  "create",
  "update",
  "delete",
  "asset",
  "runtime_commit",
  "import",
  "manual",
  "target_device",
  "template",
  "template_source",
  "error_runtime",
  "error_upstream",
  "error_input",
  "error_capability",
  "error_build",
  "error_import",
  "critical",
  "high",
  "exception",
  "medium",
  "low",
] as const;

type LegendSemanticMeta = {
  badgeStatus: LegendBadgeStatus;
  label?: string;
  labelKey?: string;
};

export const LEGEND_BADGE_COLOR_TOKEN: Record<LegendBadgeStatus, string> = {
  success: "var(--ant-colorSuccess, #52c41a)",
  error: "var(--ant-colorError, #ff4d4f)",
  warning: "var(--ant-colorWarning, #faad14)",
  processing: "var(--ant-colorInfo, #1677ff)",
  default: "var(--ant-colorTextQuaternary, #bfbfbf)",
};

type LegendSemanticInput = {
  key: string;
  semantic?: LegendSemantic | undefined;
};

export const LEGEND_SEMANTIC_REGISTRY: Record<LegendSemantic, LegendSemanticMeta> = {
  all: { badgeStatus: "processing", label: "全部" },
  total: { badgeStatus: "processing", label: "总数" },
  online: { badgeStatus: "success", label: "在线" },
  offline: { badgeStatus: "error", label: "离线" },
  unknown: { badgeStatus: "default", label: "未知" },
  enabled: { badgeStatus: "success", label: "已启用" },
  disabled: { badgeStatus: "default", label: "未启用" },
  running: { badgeStatus: "success", label: "运行中" },
  in_progress: { badgeStatus: "processing", label: "进行中" },
  completed: { badgeStatus: "success", label: "已完成" },
  finished: { badgeStatus: "default", label: "已结束" },
  failed: { badgeStatus: "error", label: "失败" },
  destroyed: { badgeStatus: "default", label: "已销毁" },
  warning: { badgeStatus: "warning", label: "告警" },
  attention: { badgeStatus: "warning", label: "需关注" },
  missing: { badgeStatus: "error", label: "缺失" },
  stale: { badgeStatus: "processing", label: "待清理" },
  draft: { badgeStatus: "warning", label: "草稿" },
  healthy: { badgeStatus: "success", label: "健康" },
  default: { badgeStatus: "warning", label: "默认" },
  normal: { badgeStatus: "default", label: "普通" },
  watch: { badgeStatus: "warning", label: "关注" },
  active: { badgeStatus: "success", label: "活跃" },
  current: { badgeStatus: "processing", label: "当前" },
  retry: { badgeStatus: "warning", label: "重试" },
  remaining: { badgeStatus: "success", label: "剩余" },
  pending: { badgeStatus: "processing", label: "处理中" },
  passed: { badgeStatus: "success", label: "已通过" },
  succeeded: { badgeStatus: "success", label: "成功" },
  published: { badgeStatus: "success", label: "已发布" },
  archived: { badgeStatus: "default", label: "已归档" },
  public: { badgeStatus: "success", label: "公开" },
  private: { badgeStatus: "default", label: "私密" },
  shared_to_users: { badgeStatus: "processing", label: "定向共享" },
  shared: { badgeStatus: "processing", label: "共享" },
  mine: { badgeStatus: "processing", label: "我的" },
  endpoint: { badgeStatus: "processing", label: "终端" },
  network: { badgeStatus: "processing", label: "网络" },
  security: { badgeStatus: "warning", label: "安全" },
  used: { badgeStatus: "processing", label: "已使用" },
  unused: { badgeStatus: "default", label: "未使用" },
  visible: { badgeStatus: "success", label: "可见" },
  hidden: { badgeStatus: "default", label: "隐藏" },
  inactive: { badgeStatus: "default", label: "未激活" },
  degraded: { badgeStatus: "warning", label: "降级" },
  mvp: { badgeStatus: "warning", label: "MVP" },
  reserved: { badgeStatus: "default", label: "预留" },
  container: { badgeStatus: "processing", label: "容器" },
  vm: { badgeStatus: "processing", label: "虚拟机" },
  simulator: { badgeStatus: "processing", label: "仿真器" },
  topology: { badgeStatus: "processing", label: "拓扑" },
  traffic_run: { badgeStatus: "processing", label: "流量执行" },
  traffic_capture: { badgeStatus: "processing", label: "流量抓取" },
  expiring: { badgeStatus: "warning", label: "即将过期" },
  expired: { badgeStatus: "warning", label: "已过期" },
  console: { badgeStatus: "processing", label: "控制台" },
  recycles: { badgeStatus: "processing", label: "回收任务" },
  operator: { badgeStatus: "processing", label: "操作者" },
  create: { badgeStatus: "success", label: "创建" },
  update: { badgeStatus: "processing", label: "更新" },
  delete: { badgeStatus: "error", label: "删除" },
  asset: { badgeStatus: "success", label: "资产来源" },
  runtime_commit: { badgeStatus: "processing", label: "运行态提交" },
  import: { badgeStatus: "warning", label: "导入" },
  manual: { badgeStatus: "default", label: "手工创建" },
  target_device: {
    badgeStatus: "success",
    labelKey: "platform.environment.common.usageTypes.target_device",
  },
  template: { badgeStatus: "processing", label: "模板" },
  template_source: {
    badgeStatus: "processing",
    labelKey: "platform.environment.common.usageTypes.template_source",
  },
  error_runtime: { badgeStatus: "error", label: "运行态错误" },
  error_upstream: { badgeStatus: "warning", label: "上游错误" },
  error_input: { badgeStatus: "warning", label: "输入错误" },
  error_capability: { badgeStatus: "warning", label: "能力错误" },
  error_build: { badgeStatus: "processing", label: "构建错误" },
  error_import: { badgeStatus: "warning", label: "导入错误" },
  critical: { badgeStatus: "error", label: "严重" },
  high: { badgeStatus: "error", label: "高危" },
  exception: { badgeStatus: "error", label: "例外" },
  medium: { badgeStatus: "warning", label: "中危" },
  low: { badgeStatus: "success", label: "低危" },
};

function isLegendSemantic(value: string): value is LegendSemantic {
  return (LEGEND_SEMANTIC_KEYS as readonly string[]).includes(value);
}

function inferLegendSemanticByKey(key: string): LegendSemantic | null {
  const rawKey = String(key ?? "").trim();
  return isLegendSemantic(rawKey) ? rawKey : null;
}

export function buildUndefinedLegendSemanticLabel(input: LegendSemanticInput): string {
  return `未定义图例语义：${input.semantic ?? input.key}`;
}

export function getLegendSemanticPresentation(input: LegendSemanticInput): {
  semantic: LegendSemantic | null;
  badgeStatus: LegendBadgeStatus;
  label?: string;
  unresolvedLabel?: string;
} {
  const semantic = input.semantic ?? inferLegendSemanticByKey(input.key);
  if (!semantic) {
    return {
      semantic: null,
      badgeStatus: "warning",
      unresolvedLabel: buildUndefinedLegendSemanticLabel(input),
    };
  }
  const meta = LEGEND_SEMANTIC_REGISTRY[semantic];
  if (!meta) {
    return {
      semantic: null,
      badgeStatus: "warning",
      unresolvedLabel: buildUndefinedLegendSemanticLabel(input),
    };
  }
  return {
    semantic,
    badgeStatus: meta.badgeStatus,
    ...(meta.labelKey || meta.label ? { label: meta.labelKey ? t(meta.labelKey) : meta.label } : {}),
  };
}

export function getLegendBadgeColor(status: LegendBadgeStatus): string {
  return LEGEND_BADGE_COLOR_TOKEN[status] || "var(--ant-colorTextQuaternary, #bfbfbf)";
}
